export const EVAS2_REPORT_WAIT_TIMEOUT_MS = 4 * 60 * 1000

export const EVAS2_REPORT_AGGREGATE_STATUS = Object.freeze({
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  NEEDS_REVIEW: 'needs_review',
  FAILED: 'failed',
})

const hasError = (value) => value != null && String(value).trim() !== ''

const result = (status, reason) => Object.freeze({ status, reason })

/**
 * 將 exact-session Report2 readback 分成產品畫面的四種狀態。
 *
 * identity 必須由 caller 依自己捕捉的 examination/session/generation 驗證；這個純函式
 * 不猜 latest，也不組 Findings／Impression。raw report failed + extract pending 是已知的
 * 收斂中狀態，原 raw error 仍留在 producer evidence，畫面繼續等 canonical presentation。
 */
export function classifyEvas2ReportAggregate(payload, { identityVerified = false } = {}) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.FAILED, 'contract_invalid')
  }
  if (identityVerified !== true) {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.PENDING, 'identity_unverified')
  }
  if (payload.found !== true) {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.PENDING, 'canonical_pending')
  }

  const reportStatus = String(payload.reportStatus || '')
  const extractStatus = String(payload.extractStatus || '')

  if (
    reportStatus === 'failed'
    && extractStatus === 'pending'
    && !hasError(payload.extractError)
  ) {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.PENDING, 'raw_report_failed_extract_pending')
  }
  if (extractStatus === 'failed') {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.FAILED, 'extract_failed')
  }
  if (reportStatus === 'failed') {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.FAILED, 'report_failed')
  }
  if (hasError(payload.reportError) || hasError(payload.extractError)) {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.FAILED, 'canonical_error')
  }
  if (reportStatus === 'pending' || extractStatus === 'pending') {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.PENDING, 'canonical_pending')
  }
  if (reportStatus !== 'succeeded' || extractStatus !== 'succeeded') {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.FAILED, 'contract_invalid')
  }

  if (
    payload.reviewRequired === false
    && payload.qualityStatus === 'accepted'
    && payload.reviewReason == null
    && payload.resolution === 'source_supported'
  ) {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.ACCEPTED, 'accepted')
  }
  if (
    payload.reviewRequired === true
    && payload.qualityStatus === 'needs_review'
    && payload.reviewReason === 'source_unresolved'
    && payload.resolution === 'source_unresolved_narrative'
  ) {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.NEEDS_REVIEW, 'source_unresolved')
  }
  if (
    payload.reviewRequired === true
    && payload.qualityStatus === 'needs_review'
    && payload.reviewReason === 'field_warnings'
    && payload.resolution === 'source_supported'
  ) {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.NEEDS_REVIEW, 'field_warnings')
  }
  if (
    payload.reviewRequired === true
    && payload.qualityStatus === 'needs_review'
    && payload.reviewReason === 'presentation_translation_required'
    && payload.resolution === 'source_supported'
  ) {
    return result(EVAS2_REPORT_AGGREGATE_STATUS.NEEDS_REVIEW, 'presentation_translation_required')
  }
  return result(EVAS2_REPORT_AGGREGATE_STATUS.FAILED, 'review_metadata_invalid')
}
